import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageSquare, Send } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TONE = {
  passenger: "bg-ink-700 text-slate-100",
  driver: "bg-gold/15 text-gold border border-gold/25",
  operator: "bg-purple-500/15 text-purple-200 border border-purple-500/25",
};

export const ChatPanel = ({ listUrl, sendUrl, mine, title = "Trip messages", compact = false }) => {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  const load = useCallback(
    () => api.get(listUrl).then(({ data }) => setMessages(data.messages)).catch(() => {}),
    [listUrl]
  );

  useEffect(() => {
    load();
    const timer = setInterval(load, 12000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post(sendUrl, { body: body.trim() });
      setBody("");
      load();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="chat-panel" className={`rounded-xl border border-ink-600 bg-ink-800 ${compact ? "p-4" : "p-6"}`}>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
        <MessageSquare className="h-3.5 w-3.5" /> {title}
      </p>
      <div data-testid="chat-messages" className="mt-4 max-h-64 space-y-2 overflow-y-auto pr-1">
        {messages.length === 0 && (
          <p data-testid="chat-empty" className="text-xs text-slate-500">
            No messages yet. Phone numbers stay private — everything runs through here.
          </p>
        )}
        {messages.map((m) => (
          <div key={m.id} data-testid={`chat-message-${m.id}`}
            className={`flex ${m.sender === mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${TONE[m.sender] || TONE.passenger}`}>
              <p className="font-mono text-[9px] uppercase tracking-widest opacity-70">
                {m.sender === mine ? "You" : m.sender_label}
              </p>
              <p className="mt-0.5 whitespace-pre-wrap break-words">{m.body}</p>
              <p className="mt-1 font-mono text-[9px] opacity-50">
                {new Date(m.at).toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="mt-4 flex gap-2">
        <Input data-testid="chat-input" value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message…"
          className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
        <Button data-testid="chat-send" type="submit" disabled={busy || !body.trim()}
          className="h-11 shrink-0 bg-gold px-4 text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
      {error && <p data-testid="chat-error" className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
};
